import React from "react";
import {
	Button,
	ButtonProps,
	Column,
	Heading,
	makeClassName,
	Modal,
	ModalProps,
	PartialKeys,
	Row,
} from "@hex-insights/core";
import { CalendarEventForm } from "../../../Forms";
import { animatedIconButtonStyles } from "../../_General";
import { calendarEventModelBackgroundColor, CalendarEventModelIcon } from "../../_General/ModelIcons";
import styles from "./styles.module.css";

export type CalendarEventCreateModalAnimatedIconControllerProps = Pick<
	CalendarEventCreateModalControllerProps,
	"onSuccess" | "disabled" | "style"
>;

export function CalendarEventCreateModalAnimatedIconController({
	style,
	...props
}: CalendarEventCreateModalAnimatedIconControllerProps) {
	return (
		<CalendarEventCreateModalController
			className={animatedIconButtonStyles["animated-icon-button"]}
			style={
				{
					"--button-background-color": calendarEventModelBackgroundColor,
					...style,
				} as React.CSSProperties
			}
			{...props}
		>
			<Column justify="spaced-start" align="center" verticalSpacing="0.5rem">
				<CalendarEventModelIcon backgroundColor="transparent" />
				<span>Event</span>
			</Column>
		</CalendarEventCreateModalController>
	);
}

export type CalendarEventCreateModalControllerProps = Pick<
	ButtonProps,
	"variant" | "size" | "title" | "disabled" | "className" | "style" | "children"
> &
	Omit<CalendarEventCreateModalProps, "ifRef" | "onClose" | "className" | "style">;

export function CalendarEventCreateModalController({
	variant,
	size,
	title,
	disabled,
	className,
	style,
	children,
	...props
}: CalendarEventCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				variant={variant}
				size={size}
				title={title}
				disabled={disabled}
				onClick={openModal}
				className={className}
				style={style}
			>
				{children}
			</Button>

			<Modal.If condition={isModalOpen}>
				<CalendarEventCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type CalendarEventCreateModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style"> &
	PartialKeys<
		Omit<CalendarEventForm.ControlledCreateProps, "className" | "style" | "columnClassName" | "columnStyle">,
		"onSuccess"
	>;

export function CalendarEventCreateModal({
	ifRef,
	onClose,
	onSuccess,
	className,
	style,
	...props
}: CalendarEventCreateModalProps) {
	const fullOnSuccess = React.useCallback<CalendarEventForm.ControlledCreateProps["onSuccess"]>(
		(formState, data) => {
			onSuccess?.(formState, data);
			onClose?.();
		},
		[onClose, onSuccess],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["create-modal"], className)} style={style}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<CalendarEventModelIcon size={22} />
					<Heading.H2 noMargin>New Event</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<CalendarEventForm.ControlledCreate onSuccess={fullOnSuccess} columnStyle={{ width: "100%" }} {...props} />
			</Modal.Body>
		</Modal>
	);
}
